import Keycloak from 'keycloak-js';

// Keycloak instance
let keycloakInstance = null;
let isInitializing = false;
let isInitialized = false;

const useKeycloak = () => {
  // Initialize Keycloak instance
  const initKeycloak = () => {
    if (!keycloakInstance) {
      keycloakInstance = new Keycloak({
        url: process.env.REACT_APP_KEYCLOAK_URL,
        realm: process.env.REACT_APP_KEYCLOAK_REALM,
        clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
      });
    }
    return keycloakInstance;
  };

  // Get current Keycloak instance
  const getKeycloak = () => {
    return keycloakInstance || initKeycloak();
  };

  // Initialize and authenticate with Keycloak
  const keycloakLogin = (onSuccess, onError) => {
    let kc = getKeycloak();

    // If already authenticated, just call success
    if (kc.authenticated) {
      onSuccess && onSuccess(kc);
      return;
    }

    // If initialized but not authenticated, reset to allow fresh login attempt
    if (isInitialized && !kc.authenticated) {
      resetKeycloakInstance();
      kc = getKeycloak(); // Get fresh instance after reset
    }

    // If already initializing, don't init again
    if (isInitializing) {
      return;
    }

    // Check if we're returning from a Keycloak redirect
    const hasKeycloakParams = window.location.search.includes('state') ||
                              window.location.search.includes('session_state') ||
                              window.location.search.includes('code') ||
                              window.location.hash.includes('state') ||
                              window.location.hash.includes('session_state') ||
                              window.location.hash.includes('code');

    const initConfig = {
      onLoad: hasKeycloakParams ? 'check-sso' : 'login-required',
      checkLoginIframe: false,
      pkceMethod: 'S256',
    };

    isInitializing = true;
    kc.init(initConfig)
      .then((authenticated) => {
        isInitializing = false;
        isInitialized = true;
        if (authenticated) {
          // Clean URL after successful authentication
          if (hasKeycloakParams) {
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          }
          onSuccess && onSuccess(kc);
        } else {
          onError && onError(new Error('Not authenticated'));
        }
      })
      .catch((error) => {
        isInitializing = false;
        // Silently handle initialization errors (expected with stale params or redirects)
        onError && onError(error);
      });
  };  // Silent check SSO
  const keycloakCheckSSO = (onSuccess, onError) => {
    const kc = initKeycloak();

    const ssoConfig = {
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      checkLoginIframe: false,
      pkceMethod: 'S256',
    };

    kc.init(ssoConfig)
      .then((authenticated) => {
        if (authenticated) {
          onSuccess && onSuccess(kc);
        } else {
          onSuccess && onSuccess(null);
        }
      })
      .catch((error) => {
        console.error('Keycloak SSO check error');
        onError && onError(error);
      });
  };

  // Logout from Keycloak
  const keycloakLogout = () => {
    const kc = getKeycloak();
    if (kc && kc.authenticated) {
      kc.logout({
        redirectUri: window.location.origin,
      });
    }
    // Reset instance after logout
    keycloakInstance = null;
    isInitializing = false;
    isInitialized = false;
  };

  // Reset Keycloak instance (useful for testing or forced re-initialization)
  const resetKeycloakInstance = () => {
    keycloakInstance = null;
    isInitializing = false;
    isInitialized = false;
  };

  // Get user info from Keycloak token
  const getKeycloakUserInfo = () => {
    const kc = getKeycloak();
    if (kc && kc.authenticated && kc.tokenParsed) {
      return {
        email: kc.tokenParsed.email,
        name: kc.tokenParsed.given_name || kc.tokenParsed.name,
        lastname: kc.tokenParsed.family_name || '',
        username: kc.tokenParsed.preferred_username,
        roles: kc.tokenParsed.realm_access?.roles || [],
      };
    }
    return null;
  };

  // Get Keycloak token
  const getKeycloakToken = () => {
    const kc = getKeycloak();
    return kc?.token || null;
  };

  // Update token if needed
  const updateKeycloakToken = async (minValidity = 30) => {
    const kc = getKeycloak();
    if (kc && kc.authenticated) {
      try {
        const refreshed = await kc.updateToken(minValidity);
        if (refreshed) {
          return kc.token;
        }
        return null;
      } catch (error) {
        console.error('Failed to refresh token');
        return null;
      }
    }
    return null;
  };

  // Check if user is authenticated with Keycloak
  const isKeycloakAuthenticated = () => {
    const kc = getKeycloak();
    return kc?.authenticated || false;
  };

  // Initialize Keycloak on app load to check existing session
  const initKeycloakOnLoad = (onSuccess, onError) => {
    const kc = getKeycloak();

    // If already authenticated, just return success
    if (kc.authenticated) {
      onSuccess && onSuccess(kc);
      return;
    }

    // If already initializing or initialized, don't init again
    if (isInitializing) {
      return;
    }

    if (isInitialized) {
      onSuccess && onSuccess(kc.authenticated ? kc : null);
      return;
    }

    const initConfig = {
      onLoad: 'check-sso',
      checkLoginIframe: false,
      pkceMethod: 'S256',
    };

    isInitializing = true;
    // Check if we're returning from a Keycloak redirect or if there's a session
    kc.init(initConfig)
      .then((authenticated) => {
        isInitializing = false;
        isInitialized = true;
        if (authenticated) {
          // Clean URL if there are Keycloak params
          const hasKeycloakParams = window.location.search.includes('state') ||
                                    window.location.search.includes('session_state') ||
                                    window.location.search.includes('code') ||
                                    window.location.hash.includes('state') ||
                                    window.location.hash.includes('session_state') ||
                                    window.location.hash.includes('code');
          if (hasKeycloakParams) {
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          }
          onSuccess && onSuccess(kc);
        } else {
          onSuccess && onSuccess(null);
        }
      })
      .catch((error) => {
        isInitializing = false;
        // Silently handle check-sso errors (expected with stale params after logout)

        // Reset instance on error to allow retry
        keycloakInstance = null;
        isInitialized = false;

        onError && onError(error);
      });
  };

  return {
    initKeycloak,
    getKeycloak,
    keycloakLogin,
    keycloakCheckSSO,
    keycloakLogout,
    getKeycloakUserInfo,
    getKeycloakToken,
    updateKeycloakToken,
    isKeycloakAuthenticated,
    initKeycloakOnLoad,
    resetKeycloakInstance,
  };
};

export default useKeycloak;
